import { useEffect, useState } from 'react';
import { getBouts } from '../api/client';
import { logBackgroundError } from '../observability';
import type { Bout, Tournament } from '../types';

/**
 * #664, F29 — the bouts that run now (status `current`) in the tournaments of
 * today, by tournament id. A tournament with no live bout has no key.
 *
 * One `getBouts` runs for each tournament. A failed read leaves that
 * tournament out and goes to telemetry; the card shows no live row, as before.
 * The hook reads again only when the SET of ids changes, so a new array of the
 * same tournaments (a country filter, a new render) does not fetch. A caller
 * that has a retry counter passes it as `reloadKey`: a retry then reads the
 * live bouts again, also when the ids are the same.
 * An answer that comes after a newer run started is dropped.
 */
export function useLiveBouts(todays: readonly Tournament[], reloadKey = 0): Record<string, Bout[]> {
  const [liveBouts, setLiveBouts] = useState<Record<string, Bout[]>>({});
  const ids = todays.map(t => t.id).join(',');

  useEffect(() => {
    let latest = true;
    const list = ids ? ids.split(',') : [];
    Promise.all(
      list.map(id =>
        getBouts(id).then(br => {
          const live = (br.data as Bout[]).filter(b => b.status === 'current');
          return live.length ? { [id]: live } : {};
        }).catch(err => {
          logBackgroundError('useLiveBouts:bouts')(err);
          return {} as Record<string, Bout[]>;
        }),
      ),
    ).then(results => {
      if (!latest) return;
      const next: Record<string, Bout[]> = Object.assign({}, ...results);
      // No live bout before and none now: keep the same object, so no render.
      setLiveBouts(prev => (Object.keys(next).length === 0 && Object.keys(prev).length === 0 ? prev : next));
    });
    return () => { latest = false; };
  }, [ids, reloadKey]);

  return liveBouts;
}
